import React, { useState, useEffect, useCallback } from "react";
import { Container, HeaderRow, List, ListItem, ListItemLabel, TitleTextWrap, ListItemActions, EditableTitleInput } from "./ChatsList.styled";
import { Typography, ButtonMenu, Skeleton } from "@ann/web-components";
import { useHomeChat } from "../../../../context";
import DeleteConversationModal from "../../../DeleteConversationModal";

const ChatsList = () => {
  const {
    selectedAgentId,
    selectedConversationId,
    conversations,
    isLoadingConversations,
    fetchConversations,
    selectConversation,
    setConversations,
    deleteConversation,
    isDeletingConversation,
    defaultAgentIdForNewChat,
  } = useHomeChat();

  const agentIdForConversations = selectedAgentId || defaultAgentIdForNewChat;

  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);

  // Cargar conversaciones: del agente seleccionado o del concierge (New Chat)
  useEffect(() => {
    if (agentIdForConversations) {
      fetchConversations(agentIdForConversations, null);
    } else {
      fetchConversations(null, null);
    }
  }, [agentIdForConversations, fetchConversations]);

  const handleItemClick = useCallback(
    (conversationUuid) => {
      selectConversation(conversationUuid);
    },
    [selectConversation]
  );

  const getConversationId = (chat) =>
    chat.conversationUuid || chat.conversation_uuid || chat.id || chat.uuid;

  const handleMenuChange = useCallback((value, chat) => {
    if (value === "delete") {
      setConversationToDelete(chat);
    }
    if (value === "rename") {
      setEditingConversationId(getConversationId(chat));
      setEditingTitle(chat.title || "Untitled");
    }
  }, []);

  const RENAME_DEMO_DELAY_MS = 2000;

  const handleSaveRename = useCallback(
    (id) => {
      const titleToSend = editingTitle.trim();
      if (!id || !titleToSend) {
        setEditingConversationId(null);
        setEditingTitle("");
        return;
      }
      setIsUpdatingTitle(true);
      setTimeout(() => {
        setConversations((prev) =>
          prev.map((c) =>
            getConversationId(c) === id ? { ...c, title: titleToSend } : c
          )
        );
        setEditingConversationId(null);
        setEditingTitle("");
        setIsUpdatingTitle(false);
      }, RENAME_DEMO_DELAY_MS);
    },
    [editingTitle, setConversations]
  );

  const handleCancelRename = useCallback(() => {
    setEditingConversationId(null);
    setEditingTitle("");
  }, []);

  const handleRenameKeyDown = useCallback(
    (e, id) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const titleToSend = editingTitle.trim();
        if (titleToSend) handleSaveRename(id);
        else handleCancelRename();
      }
      if (e.key === "Escape") {
        handleCancelRename();
      }
    },
    [editingTitle, handleSaveRename, handleCancelRename]
  );

  const handleCloseDeleteModal = useCallback(() => {
    setConversationToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(
    async (id) => {
      await deleteConversation(id);
      handleCloseDeleteModal();
    },
    [deleteConversation, handleCloseDeleteModal]
  );

  return (
    <Container>
      <HeaderRow>
        <Typography category="subtitle" size="small" weight="bold">
          Chats
        </Typography>
      </HeaderRow>

      <List>
        {isLoadingConversations ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height="36px" width="100%" />
            ))}
          </>
        ) : (conversations || []).map((chat) => (
          <ListItem
            key={getConversationId(chat)}
            $isSelected={selectedConversationId === getConversationId(chat)}
            onClick={() => {
              if (editingConversationId !== getConversationId(chat)) {
                handleItemClick(getConversationId(chat));
              }
            }}
          >
            <ListItemLabel onClick={(e) => editingConversationId && e.stopPropagation()}>
              {editingConversationId === getConversationId(chat) ? (
                isUpdatingTitle ? (
                  <Skeleton height="36px" width="100%" />
                ) : (
                  <EditableTitleInput
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onBlur={() => handleSaveRename(getConversationId(chat))}
                    onKeyDown={(e) => handleRenameKeyDown(e, getConversationId(chat))}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    placeholder="Chat title"
                    aria-label="Rename chat"
                  />
                )
              ) : (
                <TitleTextWrap>
                  <Typography
                    category="body"
                    size="medium"
                    weight="regular"
                    color="body"
                    title={chat.title || "Untitled"}
                  >
                    {chat.title || "Untitled"}
                  </Typography>
                </TitleTextWrap>
              )}
            </ListItemLabel>
            <ListItemActions onClick={(e) => e.stopPropagation()}>
              <ButtonMenu
                alignMenu="left"
                customIcon="dotsMenu2"
                iconPosition="right"
                label=""
                menuPosition="top"
                onChange={(value) => handleMenuChange(value, chat)}
                size="small"
                type="tertiary"
                usePortal
                width="auto"
                disableEffects={true}
                className="chat-actions"
              >
                <ButtonMenu.Menu icon={true}>
                  <ButtonMenu.Item label="Rename" value="rename" icon="edit">
                    Rename
                  </ButtonMenu.Item>
                  {/*<ButtonMenu.Item label="Pin chat" value="pin_chat" icon="keep">
                    Pin chat
                  </ButtonMenu.Item>
                  <ButtonMenu.Item label="Delete" value="delete" icon="delete2">
                    Delete
                  </ButtonMenu.Item>*/}
                </ButtonMenu.Menu>
              </ButtonMenu>
            </ListItemActions>
          </ListItem>
        ))}
      </List>

      <DeleteConversationModal
        isOpen={!!conversationToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        conversationId={conversationToDelete ? getConversationId(conversationToDelete) : null}
        isLoading={isDeletingConversation}
      />
    </Container>
  );
};

export default ChatsList;
